// The frame markup bands of Settings › Markup & fitting (design/screens/settings-markups.html; UC-PRICING-004): the tenant's bands
// for every frame with an example price each, the tenant's bands keyed to a supplier, a brand or a product, the acting branch's
// overrides with who set them, the line that says how bands resolve, and — for an Owner or Admin (`pricing.markup.write`) — Add
// band, a band's Edit and Retire, and Copy from tenant default. A refused band says why under the field it concerns: an overlap
// under Cost from, naming the bands it crosses (E1). The examples are the money package's `markup` and `roundPrice`
// (PricingShared.tsx) at the tenant's rounding rule; nothing here writes a price (R-09).
import { FrameMarkupBandViewSchema, PRICING_LIMITS, type FrameMarkupBandView, type ProductSummary, type RoundingRule } from "@visionx/contracts";
import { useMemo, useState, type ReactNode } from "react";
import { newIdempotencyKey } from "../api/client";
import { pricing, type BandBody } from "../api/endpoints.pricing";
import { carriedOut, describeError, isApiError } from "../api/problem";
import { formatDate, formatMoney } from "../lib/format";
import { bpFromPercentText, centsFromText, percentTextFromBp, textFromCents } from "../lib/money-input";
import { Alert, Empty, Field, Modal, Panel, Select, TextInput } from "../ui/bits";
import { ProductPicker, SupplierPicker } from "./CatalogueShared";
import {
  bandChangeWords, bandExample, bandKeyWords, bandText, byLine, costTimes, countWord, etagOf, isPlain, markupText, overlapWords, rangeText, shortName,
} from "./PricingShared";

const byStart = (a: FrameMarkupBandView, b: FrameMarkupBandView): number => a.rangeStartCents - b.rangeStartCents || a.id.localeCompare(b.id);
const faint = (t: string) => <span className="faint">{t}</span>;

/** The bands a page shows: the tenant's without a key, the tenant's with one, and the acting branch's own (UC-PRICING-004 step 1). */
export function splitBands(bands: readonly FrameMarkupBandView[], branchId: string | null) {
  const tenant = bands.filter((b) => b.branchId === null);
  return {
    tenantPlain: tenant.filter(isPlain).sort(byStart),
    tenantKeyed: tenant.filter((b) => !isPlain(b)).sort(byStart),
    branch: branchId ? bands.filter((b) => b.branchId === branchId).sort((a, b) => Number(isPlain(b)) - Number(isPlain(a)) || byStart(a, b)) : [],
  };
}

/**
 * The line above the panels (UC-PRICING-004 step 4): bands resolve tenant → branch, and what the acting branch overrides — its one
 * override in full, with who set it and when, or how many it has.
 */
export function resolutionLine(branchName: string, overrides: readonly FrameMarkupBandView[]): string {
  const head = "Bands resolve tenant → branch.";
  if (overrides.length === 0) return `${head} ${branchName} has no overrides; every frame uses the tenant bands.`;
  if (overrides.length === 1) {
    const o = overrides[0]!;
    const who = o.createdBy ? `, set by ${shortName(o.createdBy.displayName)} on ${formatDate(o.createdAt)}` : "";
    return `${head} ${branchName} has one override — ${bandKeyWords(o)}, ${rangeText(o)} → ${markupText(o.marginPercentBp, o.marginValueCents)}${who}; every other frame uses the tenant bands.`;
  }
  return `${head} ${branchName} has ${countWord(overrides.length)} overrides; each wins over the tenant bands for the frames and costs it covers, and every other frame uses the tenant bands.`;
}

/** A row's Edit and Retire, for an Owner or Admin. */
function RowActions({ b, onEdit, onRetire, disabled, title }: { b: FrameMarkupBandView; onEdit: () => void; onRetire: () => void; disabled: boolean; title: string | undefined }) {
  const what = `${bandKeyWords(b)} ${rangeText(b)}`;
  return (
    <td className="r nw">
      <button className="btn sm" type="button" disabled={disabled} title={title} aria-label={`Edit band ${what}`} onClick={onEdit}>Edit</button>{" "}
      <button className="btn sm" type="button" disabled={disabled} title={title} aria-label={`Retire band ${what}`} onClick={onRetire}>Retire</button>
    </td>
  );
}

export interface BandActions { canWrite: boolean; disabled: boolean; title: string | undefined; onEdit: (b: FrameMarkupBandView) => void; onRetire: (b: FrameMarkupBandView) => void }

/**
 * The tenant's bands for every frame (the prototype's left panel), and below them the tenant's keyed bands, if any. At a practice
 * of one store (`oneStore`) they are the practice's bands, named with no word of a tenant default or of branches (UC-PRICING-004 A7).
 */
export function TenantBandsPanel({ plain, keyed, rule, actions, oneStore = false }: { plain: readonly FrameMarkupBandView[]; keyed: readonly FrameMarkupBandView[]; rule: RoundingRule; actions: BandActions; oneStore?: boolean | undefined }) {
  const times = costTimes(plain[0]);
  return (
    <Panel title={oneStore ? "Bands — all suppliers" : "Bands — all suppliers (tenant default)"} meta={`${plain.length === 1 ? "1 band" : `${plain.length} bands`} · VAT-inclusive shelf price from supplier cost`} tight
      foot={<span className="help">Markup applies to the supplier cost and gives the VAT-inclusive shelf price directly{times ? ` (in the first band, cost × ${times} is the shelf price)` : ""}. A band's from-value is inclusive, its to-value exclusive.</span>}>
      {plain.length === 0 ? <Empty title={oneStore ? "No bands yet." : "No tenant bands yet."}>{actions.canWrite ? "Add band proposes every frame's shelf price from its cost." : "An Owner or Admin adds them."}</Empty> : (
        <div className="tbl-scroll"><table>
          <thead><tr><th className="r">Cost from</th><th className="r">Cost to</th><th className="r">Markup</th><th>Example (cost → price)</th>{actions.canWrite && <th />}</tr></thead>
          <tbody>
            {plain.map((b) => {
              const ex = bandExample(b, rule);
              return (
                <tr key={b.id}>
                  <td className="money">{formatMoney(b.rangeStartCents)}</td>
                  {b.rangeEndCents === null ? <td className="r faint">and above</td> : <td className="money">{formatMoney(b.rangeEndCents)}</td>}
                  <td className="mono r">{markupText(b.marginPercentBp, b.marginValueCents)}</td>
                  <td className="mono nw">{formatMoney(ex.cost)} → {formatMoney(ex.price)}</td>
                  {actions.canWrite && <RowActions b={b} onEdit={() => actions.onEdit(b)} onRetire={() => actions.onRetire(b)} disabled={actions.disabled} title={actions.title} />}
                </tr>
              );
            })}
          </tbody>
        </table></div>
      )}
      {keyed.length > 0 && <>
        <div className="divider" />
        <div className="panel-body"><span className="small faint">{oneStore ? "Bands for one supplier, brand or product" : "Tenant bands for one supplier, brand or product — every branch"}</span></div>
        <KeyedBandsTable bands={keyed} actions={actions} />
      </>}
    </Panel>
  );
}

/** Bands with their keys and who set them: a branch's overrides, the tenant's keyed bands. */
function KeyedBandsTable({ bands, actions }: { bands: readonly FrameMarkupBandView[]; actions: BandActions }) {
  return (
    <div className="tbl-scroll"><table>
      <thead><tr><th>Supplier</th><th>Brand</th><th className="r">Cost from</th><th className="r">Cost to</th><th className="r">Markup</th><th>Set by</th>{actions.canWrite && <th />}</tr></thead>
      <tbody>
        {bands.map((b) => (
          <tr key={b.id}>
            <td>{b.supplier ? b.supplier.name || "A supplier" : b.supplierId ? "A supplier" : isPlain(b) ? faint("All suppliers") : faint("Any")}
              {b.supplier && !b.supplier.active && <div className="small faint">inactive</div>}
              {b.productId && <div className="small faint">only <span className="mono">{b.product?.sku ?? "one product"}</span></div>}</td>
            <td>{b.brand ?? faint("—")}</td>
            <td className="money">{formatMoney(b.rangeStartCents)}</td>
            {b.rangeEndCents === null ? <td className="r faint">and above</td> : <td className="money">{formatMoney(b.rangeEndCents)}</td>}
            <td className="mono r">{markupText(b.marginPercentBp, b.marginValueCents)}</td>
            <td className="nw">{byLine(b.createdBy)}<div className="small faint">{formatDate(b.createdAt)}</div></td>
            {actions.canWrite && <RowActions b={b} onEdit={() => actions.onEdit(b)} onRetire={() => actions.onRetire(b)} disabled={actions.disabled} title={actions.title} />}
          </tr>
        ))}
      </tbody>
    </table></div>
  );
}

/** The acting branch's own bands (the prototype's right panel). */
export function OverridesPanel({ branchName, bands, actions }: { branchName: string; bands: readonly FrameMarkupBandView[]; actions: BandActions }) {
  const meta = bands.length === 0 ? "No overrides" : bands.length === 1 ? "1 override" : `${bands.length} overrides`;
  return (
    <Panel title={`Overrides — ${branchName}`} meta={meta} tight
      foot={<span className="help">At {branchName} an override wins over the tenant band for the frames and costs it covers; a band naming a product, supplier or brand wins over one that names none.</span>}>
      {bands.length === 0 ? <Empty title={`${branchName} uses the tenant bands.`}>{actions.canWrite ? `Add band with the scope ${branchName} sets an override; Copy from tenant default gives ${branchName} its own copy to change.` : "An Owner or Admin sets overrides."}</Empty>
        : <KeyedBandsTable bands={bands} actions={actions} />}
    </Panel>
  );
}

// --- Add band and Edit (UC-PRICING-004 steps 2–4, A1, A2, A5; E1–E3, E5, E7) -------------------------------------------------

type BandField = "scope" | "supplierId" | "brand" | "productId" | "rangeStartCents" | "rangeEndCents" | "marginPercentBp" | "marginValueCents";
const FIELD_OF: Readonly<Record<string, BandField>> = {
  branchId: "scope", supplierId: "supplierId", brand: "brand", productId: "productId",
  rangeStartCents: "rangeStartCents", rangeEndCents: "rangeEndCents", marginPercentBp: "marginPercentBp", marginValueCents: "marginValueCents",
};

/** A refusal under its field, in words (the API's codes, BR-PRICING-004). */
function wordsOf(field: BandField, code: string, message: string, overlaps: unknown, known: readonly FrameMarkupBandView[]): string {
  if (code === "overlap") return overlapWords(overlaps, known) ?? "Overlaps an active band of the same scope and key";
  if (code === "not_above_start") return "Must be more than Cost from";
  if (code === "not_a_frame") return "Only a frame takes a band; other products take a product markup";
  if (code === "inactive") return "That supplier is inactive; a new band can't name it";
  if (field === "marginPercentBp") return code === "out_of_range" ? `From 0% to ${PRICING_LIMITS.markupBpMax / 100}%` : "Enter the markup, e.g. 300";
  if (field === "rangeStartCents" || field === "rangeEndCents" || field === "marginValueCents") return code === "required" ? "Enter the amount, e.g. 500.00" : "Rand and cents of at least R 0.00";
  if (code === "too_long") return "At most 60 characters";
  return message;
}

/**
 * Add band (no `band`) or Edit (a band): the scope first — the organisation, or one branch — then the key, the cost range and the
 * markup; Enter saves. At a practice of one store (`oneStore`) a new band is the organisation's and asks no scope, and an
 * organisation's band names none; a band of the store's own, added before, still names it (UC-PRICING-004 A7). A version conflict keeps what was typed and says what the band is now, and the next save is made on the
 * band as it is now (E5). Saved again under its key after a lost answer and answered `409 REQUEST_COMPLETED`, it was carried out,
 * its answer not kept: `onSaved` takes no band, and the page reads the bands again (BR-GOVERNANCE-016; UC-PRICING-004 A6).
 */
export function BandDialog({ band, branches, defaultScope, known, onClose, onSaved, oneStore = false }: {
  band: FrameMarkupBandView | null; branches: readonly { id: string; name: string }[]; defaultScope: string; known: readonly FrameMarkupBandView[];
  onClose: () => void; onSaved: (b: FrameMarkupBandView | null, added: boolean) => void; oneStore?: boolean | undefined;
}) {
  const [base, setBase] = useState<FrameMarkupBandView | null>(band);
  // No scope to choose or to name at a practice of one store: a new band is the organisation's, as is a band without a branch.
  const noScope = oneStore && (band === null || band.branchId === null);
  const [scope, setScope] = useState(band ? band.branchId ?? "org" : oneStore ? "org" : defaultScope);
  const [supplier, setSupplier] = useState<{ id: string; name: string } | null>(band?.supplier ? { id: band.supplier.id, name: band.supplier.name } : null);
  const [brand, setBrand] = useState(band?.brand ?? "");
  const [product, setProduct] = useState<ProductSummary | null>(band?.product ? ({ id: band.product.id, sku: band.product.sku, displayName: band.product.name, name: band.product.name } as ProductSummary) : null);
  const [from, setFrom] = useState(band ? textFromCents(band.rangeStartCents) : "");
  const [to, setTo] = useState(band && band.rangeEndCents !== null ? textFromCents(band.rangeEndCents) : "");
  const [pct, setPct] = useState(band ? percentTextFromBp(band.marginPercentBp) : "");
  const [value, setValue] = useState(band && band.marginValueCents > 0 ? textFromCents(band.marginValueCents) : "");
  const [errs, setErrs] = useState<Partial<Record<BandField, string>>>({});
  const [error, setError] = useState<unknown>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const parsed = useMemo(() => {
    const issues: Partial<Record<BandField, string>> = {};
    const start = centsFromText(from);
    if (from.trim() === "") issues.rangeStartCents = "Enter the cost the band starts at, e.g. 500.00";
    else if (start === null) issues.rangeStartCents = "Rand and cents of at least R 0.00";
    const end = to.trim() === "" ? null : centsFromText(to);
    if (to.trim() !== "" && end === null) issues.rangeEndCents = "Rand and cents, or leave it empty for “and above”";
    else if (end !== null && start !== null && end <= start) issues.rangeEndCents = "Must be more than Cost from";
    const percent = bpFromPercentText(pct);
    if (pct.trim() === "") issues.marginPercentBp = "Enter the markup, e.g. 300";
    else if (percent === null || percent > PRICING_LIMITS.markupBpMax) issues.marginPercentBp = `A percentage from 0% to ${PRICING_LIMITS.markupBpMax / 100}%`;
    const val = value.trim() === "" ? 0 : centsFromText(value);
    if (val === null) issues.marginValueCents = "Rand and cents of at least R 0.00";
    return { issues, start, end, percent, val };
  }, [from, to, pct, value]);

  const body = useMemo((): BandBody | null => {
    const { issues, start, end, percent, val } = parsed;
    if (Object.keys(issues).length > 0 || start === null || percent === null || val === null) return null;
    const brandText = brand.trim();
    if (!base) {
      return {
        branchId: scope === "org" ? null : scope, rangeStartCents: start, rangeEndCents: end, marginPercentBp: percent, marginValueCents: val,
        ...(supplier ? { supplierId: supplier.id } : {}), ...(brandText ? { brand: brandText } : {}), ...(product ? { productId: product.id } : {}),
      };
    }
    // A change sends what differs from the band as it was read (or as the conflict said it is now).
    const out: BandBody = {};
    if (start !== base.rangeStartCents) out.rangeStartCents = start;
    if (end !== base.rangeEndCents) out.rangeEndCents = end;
    if (percent !== base.marginPercentBp) out.marginPercentBp = percent;
    if (val !== base.marginValueCents) out.marginValueCents = val;
    if ((supplier?.id ?? null) !== base.supplierId) out.supplierId = supplier?.id ?? null;
    if ((brandText || null) !== (base.brand ?? null)) out.brand = brandText || null;
    if ((product?.id ?? null) !== base.productId) out.productId = product?.id ?? null;
    return out;
  }, [parsed, base, scope, supplier, brand, product]);
  // One key per band asked for: a retry after a lost answer is the same request; a changed field a new one.
  const key = useMemo(() => newIdempotencyKey(), [JSON.stringify(body), base?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  const clear = (f: BandField) => setErrs((x) => { const { [f]: _gone, ...rest } = x; return rest; });
  const submit = async () => {
    if (busy) return;
    setError(null);
    if (Object.keys(parsed.issues).length > 0 || !body) { setErrs(parsed.issues); return; }
    setErrs({});
    if (base && Object.keys(body).length === 0) { onClose(); return; }
    setBusy(true);
    try {
      if (base) {
        const { data } = await pricing.changeBand(base.id, body, etagOf(base), key);
        onSaved(data, false);
      } else {
        const { data } = await pricing.addBand(body, key);
        onSaved(data, true);
      }
    } catch (err) {
      if (carriedOut(err)) {
        onSaved(null, !base);
      } else if (isApiError(err) && err.code === "VALIDATION_FAILED") {
        const next: Partial<Record<BandField, string>> = {};
        for (const f of err.errors) {
          const field = FIELD_OF[f.path];
          if (field && !next[field]) next[field] = wordsOf(field, f.code, f.message, err.meta.overlaps, known);
        }
        if (Object.keys(next).length > 0) setErrs(next);
        else setError(err);
      } else if (isApiError(err) && err.code === "VERSION_CONFLICT" && base) {
        const now = FrameMarkupBandViewSchema.safeParse(err.meta.current);
        if (now.success) { setConflict(`Someone changed this band — it is now ${bandChangeWords(base, now.data)}.`); setBase(now.data); }
        else setError(err);
      } else setError(err);
    } finally { setBusy(false); }
  };

  const scopeName = (sc: string) => (sc === "org" ? "the organisation" : branches.find((b) => b.id === sc)?.name ?? "the branch");
  return (
    <Modal title={band ? "Edit band" : "Add band"} meta={band ? `${band.branch ? `${band.branch.name} override · ` : noScope ? "" : "tenant band · "}${bandText(band)}` : "frames' shelf price from their cost"} onClose={onClose} onSubmit={() => void submit()} wide footer={<>
      <span className="help">Changes no price already set; later proposals use it (R-09).</span>
      <button className="btn right" type="button" onClick={onClose}>Cancel</button>
      <button className="btn primary" type="submit" disabled={busy}>{busy ? "Saving…" : band ? "Save band" : "Add band"}</button>
    </>}>
      {conflict && <Alert tone="warn" role="alert"><b>{conflict}</b> What you typed is kept; Save band saves it on the band as it is now.</Alert>}
      {error !== null && <Alert tone="crit" role="alert"><b>Not saved.</b> {isApiError(error) && error.code === "NOT_FOUND" ? "This band was retired meanwhile, or its branch or key is not the organisation's." : describeError(error)}</Alert>}
      {!noScope && <div className="grid-2">
        <Field label="Scope" htmlFor="band-scope" error={errs.scope} help={band ? "A band keeps its scope; retire it and add one for another scope" : scope === "org" ? "The organisation's band applies at every branch without an override" : `${scopeName(scope)}'s own band: at ${scopeName(scope)} it wins over the organisation's for the frames and costs it covers`}>
          {band
            ? <TextInput id="band-scope" disabled value={band.branch ? `${band.branch.name} only` : "Organisation — every branch"} />
            : <Select id="band-scope" value={scope} onChange={(e) => { setScope(e.target.value); clear("scope"); }}>
              <option value="org">Organisation — every branch</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name} only</option>)}
            </Select>}
        </Field>
        <div />
      </div>}
      <div className="small faint" style={{ margin: "4px 0 8px" }}>{noScope ? "Only for — leave all three empty for every frame" : <>Only for — leave all three empty for every frame of {scopeName(scope)}</>}</div>
      <div className="grid-3">
        <SupplierPicker id="band-supplier" label="Supplier" value={supplier} error={errs.supplierId} onPick={(sp) => { setSupplier(sp ? { id: sp.id, name: sp.name } : null); clear("supplierId"); }} />
        <Field label="Brand" htmlFor="band-brand" error={errs.brand} help="Matched to a frame's brand, ignoring case">
          <TextInput id="band-brand" value={brand} maxLength={60} autoComplete="off" error={!!errs.brand} onChange={(e) => { setBrand(e.target.value); clear("brand"); }} />
        </Field>
        <ProductPicker id="band-product" label="Product" categories={["Frame"]} value={product} error={errs.productId} help="One frame" onPick={(p) => { setProduct(p); clear("productId"); }} />
      </div>
      <div className="grid-2">
        <Field label="Cost from" required htmlFor="band-from" error={errs.rangeStartCents} help="Supplier cost ex VAT, inclusive">
          <TextInput id="band-from" mono inputMode="decimal" placeholder="e.g. 500.00" value={from} error={!!errs.rangeStartCents} onChange={(e) => { setFrom(e.target.value); clear("rangeStartCents"); }} />
        </Field>
        <Field label="Cost to" htmlFor="band-to" error={errs.rangeEndCents} help="Exclusive; empty for “and above”">
          <TextInput id="band-to" mono inputMode="decimal" placeholder="and above" value={to} error={!!errs.rangeEndCents} onChange={(e) => { setTo(e.target.value); clear("rangeEndCents"); }} />
        </Field>
        <Field label="Markup %" required htmlFor="band-pct" error={errs.marginPercentBp} help="On the cost: 400 gives cost × 5">
          <TextInput id="band-pct" mono inputMode="decimal" placeholder="e.g. 300" value={pct} error={!!errs.marginPercentBp} onChange={(e) => { setPct(e.target.value); clear("marginPercentBp"); }} />
        </Field>
        <Field label="Plus a value" htmlFor="band-value" error={errs.marginValueCents} help="Optional, added in VAT-inclusive rand">
          <TextInput id="band-value" mono inputMode="decimal" placeholder="0.00" value={value} error={!!errs.marginValueCents} onChange={(e) => { setValue(e.target.value); clear("marginValueCents"); }} />
        </Field>
      </div>
    </Modal>
  );
}

/**
 * UC-PRICING-004 A3: retire under the band's version — it stops resolving and keeps its row and history. Retired again under its key
 * after a lost answer and answered `409 REQUEST_COMPLETED`, it was carried out: `onRetired(true)`, the page reading the bands again.
 */
export function RetireBandDialog({ band, onClose, onRetired, oneStore = false }: { band: FrameMarkupBandView; onClose: () => void; onRetired: (answerLost?: boolean) => void; oneStore?: boolean | undefined }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const key = useMemo(() => newIdempotencyKey(), [band.id, band.version]);
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try { await pricing.retireBand(band.id, etagOf(band), key); onRetired(); }
    catch (err) { if (carriedOut(err)) onRetired(true); else setError(err); }
    finally { setBusy(false); }
  };
  const words: ReactNode = isApiError(error) && error.code === "VERSION_CONFLICT" ? "Someone changed this band since it was read; close this to see it as it is now."
    : isApiError(error) && error.code === "NOT_FOUND" ? "This band was retired meanwhile." : describeError(error);
  return (
    <Modal title="Retire band" meta={`${band.branch ? `${band.branch.name} override · ` : oneStore ? "" : "tenant band · "}${bandKeyWords(band)}`} onClose={onClose} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={onClose}>Cancel</button>
      <button className="btn danger" type="submit" disabled={busy}>{busy ? "Retiring…" : "Retire"}</button>
    </>}>
      {error !== null && <Alert tone="crit" role="alert"><b>Not retired.</b> {words}</Alert>}
      <p><b>Retire this band? Frames it covered fall to the next band.</b></p>
      <p className="small">{bandText(band)} · {bandKeyWords(band)}. It is kept with its history and never resolves again; no price already set changes.</p>
    </Modal>
  );
}
